/* eslint-disable no-restricted-syntax, no-continue */
import { getPlayerProjections, getTeamPlayerInfo, getTeamRosterInfo } from '@/api/requests';
import { TPlayerRole } from '@/constants';
import { IPlayerOption, ITeamPlayers, ITeamPlayersCache, TTeam } from '@/types';
import { orderBy, find, compact } from 'lodash';
import { useToast } from 'vue-toastification';

const toast = useToast();

const resolvePlayerInfo = (
  playerIds: number[],
  roster: any,
  playersInfo: any,
  role: TPlayerRole
): IPlayerOption[] => {
  const info = [];
  let count = 0;

  for (const playerId of playerIds) {
    if (count >= 3) {
      break;
    }

    const rosterPlayer = find(roster, (p) => p.playerId === playerId);
    const playerInfo = find(playersInfo, (p) => p.playerId === playerId);

    // exclude the QB from rusher list
    if (!playerInfo || !rosterPlayer) {
      continue;
    }
    if (role === TPlayerRole.RUSHER && playerInfo.positionCategory === 1) {
      continue;
    }

    info.push({
      playerId: playerId as number,
      name: `${playerInfo.firstName.slice(0, 1)}. ${playerInfo.lastName}`,
      firstName: playerInfo.firstName,
      lastName: playerInfo.lastName,
      number: rosterPlayer.jersey,
    });

    count += 1;
  }

  return info;
};

const MAX_RETRIES = 3;
// eslint-disable-next-line consistent-return
async function requestRetry(func: Function): Promise<any> {
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const resp = await func();
      return resp;
    } catch (error) {
      if (attempt >= MAX_RETRIES) {
        throw error;
      }
    }
  }
}

const ROSTER_CACHE_KEY = 'live-cfb-roster';
const retrieveCachedOrInitializePlayers = (gameId: number): ITeamPlayers => {
  try {
    const cachedPlayers = JSON.parse(localStorage.getItem(ROSTER_CACHE_KEY)) as ITeamPlayersCache;
    if (cachedPlayers && cachedPlayers.gameId === gameId) {
      return cachedPlayers.players;
    }
  } catch (error) {
    localStorage.removeItem(ROSTER_CACHE_KEY);
  }

  return {
    home: {
      Passer: [],
      Rusher: [],
      Receiver: [],
    },
    away: {
      Passer: [],
      Rusher: [],
      Receiver: [],
    },
    all: [],
  };
};

export const retrievePlayerData = async (
  gameId: number,
  season: string,
  weekNum: number,
  awayTeamId: number,
  homeTeamId: number
): Promise<ITeamPlayers> => {
  const playerData = retrieveCachedOrInitializePlayers(gameId);

  if (playerData.all.length) {
    return playerData;
  }

  try {
    const responses = await Promise.allSettled([
      requestRetry(() => getPlayerProjections(season, weekNum, awayTeamId)),
      requestRetry(() => getPlayerProjections(season, weekNum, homeTeamId)),
      requestRetry(() => getTeamRosterInfo(season, awayTeamId)),
      requestRetry(() => getTeamRosterInfo(season, homeTeamId)),
      requestRetry(() => getTeamPlayerInfo(season, awayTeamId)),
      requestRetry(() => getTeamPlayerInfo(season, homeTeamId)),
    ]);

    if (responses.some((resp) => resp.status === 'rejected')) {
      throw new Error('Player data retrieval error');
    }

    const [
      awayTeamProjectionsResponse,
      homeTeamProjectionsResponse,
      awayTeamRosterResponse,
      homeTeamRosterResponse,
      awayTeamPlayerInfoResponse,
      homeTeamPlayerInfoResponse,
    ] = responses as PromiseFulfilledResult<any>[];

    const roster = {
      away: awayTeamRosterResponse.value,
      home: homeTeamRosterResponse.value,
    };
    const playersInfo = {
      away: awayTeamPlayerInfoResponse.value,
      home: homeTeamPlayerInfoResponse.value,
    };

    let side = TTeam.AWAY;
    for (const projections of [awayTeamProjectionsResponse.value, homeTeamProjectionsResponse.value]) {
      let passerMax = 0;
      let passer: number;
      const rusherMax: { [playerId: number]: number } = {};
      const receiverMax: { [playerId: number]: number } = {};

      for (const data of projections) {
        if (data.statType === 'P' && data.stat === 'Yards' && data.projected > passerMax) {
          passer = data.playerId;
          passerMax = data.projected;
        } else if (data.statType === 'RU' && data.stat === 'Yards') {
          rusherMax[data.playerId] = rusherMax[data.playerId]
            ? Math.max(rusherMax[data.playerId], data.projected)
            : data.projected;
        } else if (data.statType === 'RE' && data.stat === 'Yards') {
          receiverMax[data.playerId] = receiverMax[data.playerId]
            ? Math.max(receiverMax[data.playerId], data.projected)
            : data.projected;
        }
      }

      const topRushers = orderBy(Object.entries(rusherMax), ([_, max]) => max, ['desc']).map(([id, _]) =>
        parseInt(id, 10)
      );
      const topReceivers = orderBy(Object.entries(receiverMax), ([_, max]) => max, ['desc']).map(([id, _]) =>
        parseInt(id, 10)
      );

      const passers = resolvePlayerInfo([passer], roster[side], playersInfo[side], TPlayerRole.PASSER);
      const rushers = resolvePlayerInfo(
        topRushers,
        roster[side],
        playersInfo[side],
        TPlayerRole.RUSHER
      ).slice(0, 2);

      // Exclude QB and RBs from receiver candidates to prevent duplicate buttons
      const rusherAndQbIds = new Set(compact([passer, ...rushers.map((r) => r.playerId)]));
      const filteredTopReceivers = topReceivers.filter((id) => !rusherAndQbIds.has(id));

      const receivers = resolvePlayerInfo(
        filteredTopReceivers,
        roster[side],
        playersInfo[side],
        TPlayerRole.RECEIVER
      ).slice(0, 3);

      playerData[side] = {
        Passer: compact(passers),
        Rusher: compact([passers[0], ...rushers]),
        Receiver: compact(receivers),
      };

      playerData.all.push(...passers, ...rushers, ...receivers);

      side = TTeam.HOME;
    }

    localStorage.setItem(
      ROSTER_CACHE_KEY,
      JSON.stringify({
        gameId,
        players: playerData,
      })
    );

    return playerData;
  } catch (e) {
    console.log(e);
    toast.error('Error retrieving player data. Please try refreshing or contact SIS if problem continues.', {
      position: 'bottom-center',
      timeout: 6000,
    });

    return playerData;
  }
};
